import { useMemo, createElement } from 'react';

import { domToReact } from 'html-react-parser';
import Zoom from 'react-medium-image-zoom';
import styled from 'styled-components';
import '@/styles/vendors/image-zoom.css';

import { tierOneHeadings } from './constants';
import { convertStylesStringToObject } from './utils';
import Anchor from '@/ui/components/Anchor';
import Callout from '@/ui/components/Callout';
import InfoBox from '@/ui/components/InfoBox';
import { replaceLocalUrl } from '@/utils/helpers';
import { slugify } from '@/utils/slugify';

/**
 * @todo refactor
 */
// const DynamicSyntaxHighlighter = dynamic<SyntaxHighlighterProps>(
//   () => import('react-syntax-highlighter').then(module => module.Prism),
//   {
//     loading: () => null, // <Loader />,
//   }
// );

interface ContentNode {
  type?: string;
  data?: string;
  children?: ContentNode[];
  name?: string;
  parent?: ContentNode;
  attribs?: Record<string, string>;
}

const getTextFromChildren = (children: ContentNode[]): string => {
  if (!children) return '';
  return children
    .map(child => {
      if (child.type === 'text') {
        return child.data || '';
      }
      if (child.children) {
        return getTextFromChildren(child.children);
      }
      return '';
    })
    .join('');
};

const TableWrapper = styled.div`
  max-width: 100%;
  overflow: auto;
  position: relative;
  width: 100%;
`;

const Table = styled.table`
  border-color: #ccc;
  max-width: 100%;
  table-layout: fixed;
  white-space: nowrap;
  width: 100%;

  th,
  td {
    border-color: #ccc;
  }

  img {
    // width: 100%;
    max-width: 100%;
  }

  tr:first-child {
    white-space: normal;
  }
`;

const findParent = (currentNode: ContentNode, nodeName: string): ContentNode | null => {
  let parent = currentNode.parent;
  while (parent) {
    if (parent.name === nodeName) {
      return parent;
    }
    parent = parent.parent;
  }
  return null;
};

const replace = node => {
  // handle max-width for images
  if (node.name === 'img') {
    const maxWidthStyle = 'max-width: 100%;';

    if (typeof node.attribs.style === 'string') {
      node.attribs.style = `${node.attribs.style}; ${maxWidthStyle}`;
    } else {
      node.attribs.style = maxWidthStyle;
    }

    if (node.parent.name === 'p') {
      node.parent.attribs.style = 'max-width: 815px;';
    }
  }

  if (node.name === 'img' && (node.attribs?.srcset || node.attribs?.srcSet)) {
    const srcSet = node.attribs?.srcset || node.attribs?.srcSet;
    // Check if srcset has a valid value (not just alt text accidentally placed in srcset)
    const srcsetValue = srcSet.trim();

    // Basic regex to validate srcset format
    if (srcsetValue && /\S+\s+\d+[wx]/.test(srcsetValue)) {
      node.attribs.srcSet = srcSet;
    } else {
      if (typeof window !== 'undefined') {
        console.warn('Invalid srcset format detected a removed:', srcSet);
      }
      delete node.attribs.srcSet;
    }

    delete node.attribs.srcset;
  }

  if (node.name === 'img' && node?.attribs?.src) {
    node.attribs.src = node.attribs.src.replace('http:', 'https:');
    if (node.attribs.srcSet) {
      node.attribs.srcSet = typeof node.attribs?.srcSet === 'string' && node.attribs.srcSet.replace(/http:/g, 'https:');
    }

    node.attribs.style += '; cursor: pointer;';
  }

  // Handle images
  if (node.type === 'tag' && node.name === 'img' && node.attribs && node.attribs.src) {
    node.attribs.src = replaceLocalUrl(node.attribs.src);

    if (node.attribs.srcSet) {
      node.attribs.srcSet = replaceLocalUrl(node.attribs.srcSet);
    }
  }

  if (node?.name === 'img') {
    const imageAttribs = { ...node.attribs };
    const inlineStyleObject = imageAttribs?.style ? convertStylesStringToObject(imageAttribs.style) : {};

    delete imageAttribs.style;

    return (
      <Zoom wrapElement="span">
        <img {...imageAttribs} style={inlineStyleObject} />
      </Zoom>
    );
  }

  // Add anchor to headers - use memoized text extraction
  if (tierOneHeadings.indexOf(node.name) >= 0) {
    const text = getTextFromChildren(node.children);
    const id = slugify(text);

    return (
      <Anchor id={id} node={{ name: node.name, children: node.children }}>
        {domToReact(node.children)}
      </Anchor>
    );
  }

  // Fix video not reloading
  if (node.name === 'video') {
    const src = node.children?.[0]?.attribs?.src; // adjustSrc
    const attrs = { ...node.attribs };

    // Extract key from attrs to pass it directly
    const key = src;
    delete attrs.key;

    // iPhone safari fix https://stackoverflow.com/a/36348909/3590238
    attrs.playsInline = 'playsinline';
    attrs.muted = 'muted';

    if (attrs?.autoplay) {
      attrs.autoPlay = attrs.autoplay;
      delete attrs.autoplay;
    }

    delete attrs.style;

    return (
      <video key={key} {...attrs}>
        {domToReact(node.children)}
      </video>
    );
  }

  // Add icons to "hints"
  if (node.name === 'div' && (
    node?.attribs?.className?.endsWith('callout') ||
    node?.attribs?.class?.endsWith('callout')
  )) {
    const className = node.attribs.className || node.attribs.class;
    return (
      <Callout type={className} margin="0.5rem 0">
        {domToReact(node.children)}
      </Callout>
    );
  }

  // Calculators
  // if (node.name === 'div' && node?.attribs?.class?.startsWith('calculator')) {
  //   const calculatorType = node.attribs.class.split(' ').slice(-1)?.[0];
  //   switch (calculatorType) {
  //     case 'filament':
  //       return <FilamentCalculator />;
  //     case 'layer':
  //       return <OptimalLayerCalculator />;
  //     case 'steps-belt':
  //       return <StepsBeltCalculator />;
  //     case 'steps-screw':
  //       return <StepsLeadScrewCalculator />;
  //     case 'acceleration':
  //       return <AccelerationCalculator />;
  //     case 'price':
  //       return <PriceCalculator />;
  //     default:
  //       return <div>Invalid calculator type</div>;
  //   }
  // }

  // if (
  //   node.name === 'span' &&
  //   node?.attribs?.class &&
  //   node.attribs.class === 'tooltip'
  // ) {
  //   return (
  //     <Tooltip slug={node.attribs['data-tooltip']}>
  //       {domToReact(node.children, options())}
  //     </Tooltip>
  //   );
  // }

  // Replace http with https for images

  // Tables
  if (node.name === 'table') {
    const inlineStyleObject = node.attribs?.style ? convertStylesStringToObject(node.attribs.style) : {};

    // Add border-width if border attribute exists
    if (node.attribs?.border) {
      inlineStyleObject['border-width'] = node.attribs.border + 'px';
    }

    return (
      <TableWrapper>
        <Table className={`table ${node.attribs?.class || ''}`} style={inlineStyleObject}>
          {domToReact(node.children, { replace })}
        </Table>
      </TableWrapper>
    );
  }

  if (node.name === 'th' || node.name === 'td') {
    const tableParent = findParent(node, 'table');
    const inlineStyleObject = tableParent?.attribs?.style ? convertStylesStringToObject(tableParent.attribs.style) : {};
    const styleWithOnlyBorderStyle = {
      ...(inlineStyleObject['borderStyle'] ? { 'border-style': inlineStyleObject['borderStyle'] } : {}),
      ...(inlineStyleObject['borderColor'] ? { 'border-color': inlineStyleObject['borderColor'] } : {}),
      ...(tableParent?.attribs?.border ? { 'border-width': tableParent.attribs.border + 'px' } : {}),
    };

    // Preserve all original attributes including rowspan, colspan, etc.
    const attrs = { ...node.attribs };

    // Parse the cell's original inline styles and merge with border styles
    const cellInlineStyles = attrs.style ? convertStylesStringToObject(attrs.style) : {};
    const mergedStyles = { ...cellInlineStyles, ...styleWithOnlyBorderStyle };
    
    // Ensure React-compatible attribute names - handle both cases
    const finalProps = { 
      ...attrs, 
      style: mergedStyles 
    };

    return createElement(node.name, finalProps, domToReact(node.children, { replace }));
  }

  /**
   * @todo refactor
   */
  // Code highlighting
  // if (node.name === 'code') {
  //   const codeString = node.children
  //     .filter(child => child.type === 'text')
  //     .map(child => child.data)
  //     .join('');

  //   return (
  //     <DynamicSyntaxHighlighter language="c" style={prism}>
  //       {codeString}
  //     </DynamicSyntaxHighlighter>
  //   );
  // }

  // Handle headings
  // if (node.type === 'tag' && tierOneHeadings.includes(node.name)) {
  //   const text = getTextFromChildren(node.children);
  //   const id = slugify(text);
  //   node.attribs.id = id;
  //   node.attribs['data-visible-id'] = id;
  // }

  // Handle div.tip
  if (node.type === 'tag' && node.name === 'div' && node.attribs?.class === 'tip') {
    let title = '';
    let description = '';

    node.children?.forEach(child => {
      if (child.type === 'tag' && child.name === 'p') {
        const text = getTextFromChildren(child.children);
        if (child.attribs?.class?.includes('tip_hl')) {
          title = text;
        } else {
          description += text + ' ';
        }
      } else if (child.type === 'text' && child.data) {
        description += child.data.trim() + ' ';
      }
    });

    return <InfoBox title={title} description={description.trim()} />;
  }
};

export const useParseOptions = () => {
  return useMemo(
    () => ({
      replace,
    }),
    []
  );
};

export const options = useParseOptions;
